import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import BestOddsService from '../services/BestOddsService';
import useBestOddsFilters from '../hooks/useBestOddsFilters';
import { DEFAULT_BEST_ODDS_FILTERS } from '../utils/bestOddsFilters';

/**
 * BestOddsContext - Context for the "Best Odds / Market Anomalies" module.
 *
 * Deliberately independent from SurebetContext: Arbitrage and Best Odds are
 * two separate products in the UI and must never share state or a data
 * pipeline. Anomalies are always sorted by highest spread first (cards
 * "appear and disappear" dynamically as bookmakers correct their odds).
 *
 * Client-side filters (spread / search / bookmakers + v1.1 placeholders) live
 * here via useBestOddsFilters and persist under localStorage key bestOddsFilters.
 */

const DEFAULT_BEST_ODDS_STATE = {
  anomalies: [],
  filteredAnomalies: [],
  filters: DEFAULT_BEST_ODDS_FILTERS,
  filteredCount: 0,
  hasActiveFilters: false,
  loading: false,
  error: null,

  lastRefresh: null,
  isRefreshing: false,
  autoRefreshEnabled: true,
  refreshInterval: 600000 // 10 minutes, matches SurebetContext cadence
};

const BestOddsContext = createContext(DEFAULT_BEST_ODDS_STATE);

export const BestOddsProvider = ({ children }) => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const refreshInterval = 600000;

  const {
    filters,
    filteredAnomalies,
    filteredCount,
    hasActiveFilters,
    setFilters,
    setMinSpread,
    setMaxOdds,
    setSearchQuery,
    setSelectedBookmakers,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetFilters
  } = useBestOddsFilters(anomalies);

  /**
   * Anomalies are always sorted by highest spread first - the card grid's
   * "most dramatic bookmaker error" belongs top-left.
   */
  const sortBySpreadDesc = useCallback((items) => {
    if (!items || items.length === 0) {
      return [];
    }
    return [...items].sort((a, b) => (b.spread || 0) - (a.spread || 0));
  }, []);

  const statistics = useMemo(() => {
    const totalAnomalies = anomalies.length;
    const totalSpread = anomalies.reduce((sum, item) => sum + (item.spread || 0), 0);
    const averageSpread = totalAnomalies > 0 ? totalSpread / totalAnomalies : 0;
    const topSpread = totalAnomalies > 0 ? anomalies[0].spread || 0 : 0;

    return {
      totalAnomalies,
      averageSpread: parseFloat(averageSpread.toFixed(2)),
      topSpread: parseFloat(topSpread.toFixed(2))
    };
  }, [anomalies]);

  const loadAnomalies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await BestOddsService.getAnomalies({ useCache: false });

      if (response.error) {
        throw new Error(response.error);
      }

      const sorted = sortBySpreadDesc(response.data);
      setAnomalies(sorted);
      setLastRefresh(new Date());
      setLoading(false);

      return { data: sorted, error: null };
    } catch (err) {
      const errorMessage = err.message || 'Failed to load best odds data';
      setError(errorMessage);
      setLoading(false);

      return { data: null, error: errorMessage };
    }
  }, [sortBySpreadDesc]);

  const refreshAnomalies = useCallback(async () => {
    if (isRefreshing || loading) {
      return { data: null, error: 'Refresh already in progress' };
    }

    setIsRefreshing(true);
    const result = await loadAnomalies();
    setIsRefreshing(false);

    return result;
  }, [isRefreshing, loading, loadAnomalies]);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled((prev) => !prev);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Auto-refresh every 10 minutes, matching the Arbitrage module's cadence.
   */
  useEffect(() => {
    if (!autoRefreshEnabled) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (!isRefreshing && !loading) {
        loadAnomalies();
      }
    }, refreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [autoRefreshEnabled, isRefreshing, loading, loadAnomalies, refreshInterval]);

  useEffect(() => {
    loadAnomalies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    anomalies,
    filteredAnomalies,
    filters,
    filteredCount,
    hasActiveFilters,
    loading,
    error,

    lastRefresh,
    isRefreshing,
    autoRefreshEnabled,
    refreshInterval,

    ...statistics,

    loadAnomalies,
    refreshAnomalies,
    toggleAutoRefresh,
    clearError,

    setFilters,
    setMinSpread,
    setMaxOdds,
    setSearchQuery,
    setSelectedBookmakers,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetFilters
  }), [
    anomalies,
    filteredAnomalies,
    filters,
    filteredCount,
    hasActiveFilters,
    loading,
    error,
    lastRefresh,
    isRefreshing,
    autoRefreshEnabled,
    statistics,
    loadAnomalies,
    refreshAnomalies,
    toggleAutoRefresh,
    clearError,
    setFilters,
    setMinSpread,
    setMaxOdds,
    setSearchQuery,
    setSelectedBookmakers,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetFilters
  ]);

  return (
    <BestOddsContext.Provider value={value}>
      {children}
    </BestOddsContext.Provider>
  );
};

BestOddsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useBestOdds = () => {
  const context = useContext(BestOddsContext);

  if (!context) {
    throw new Error('useBestOdds must be used within a BestOddsProvider');
  }

  return context;
};

export default BestOddsContext;
